import { groupBy, takeWhile, uniq, uniqBy } from 'lodash';
import {
  getContextComponentIds,
  type DirectedTripleWithFilters,
  type LoadedState,
  type MetaModel,
  type MetaModelTriple,
  type Path,
  type TraversalLoadedState,
} from '@ardoq/api-types';
import { toTripleKey } from 'architectureModel/metaModelOperations';
import { getLinkedType } from 'traversals/pathAndGraphUtils';
import { pathOperations } from 'traversals/pathOperations';
import { toBELabel } from 'viewpointBuilder/traversals/getPaths';
import { pathToKey } from 'viewpointBuilder/traversals/pathToKey';
import {
  hasMatches,
  isConcreteStep,
  isConcreteTriple,
  isLoadedTraversal,
} from './predicates';
import type {
  ComponentHit,
  MetamodelTraversal,
  PseudoTraversal,
  PseudoTraversalNode,
  PseudoTraversalPath,
  PseudoTraversalStep,
  PseudoTraversalTriple,
  ResolvedBlock,
  TraversalConstruction,
  TraversalMatches,
  TraversalMatching,
  WalkSteps,
} from './types';

const MAX_HOPS_PER_GAP = 5;

const normalizeName = (name: string): string => name.trim().toLowerCase();

/** The step's names in the order the path walks them: from, then to. */
const orderByWalkDirection = ({
  sourceTypeOrInstanceName,
  targetTypeOrInstanceName,
  direction,
}: PseudoTraversalTriple): [string, string] =>
  direction === 'outgoing'
    ? [sourceTypeOrInstanceName, targetTypeOrInstanceName]
    : [targetTypeOrInstanceName, sourceTypeOrInstanceName];

/**
 * Every node of a pseudo-traversal path after its start, in walk order, one
 * per step end, with the step that reaches it. The triples need not chain, so
 * a step's walk start counts too, reached through a gap, unless it is the
 * previous step's end.
 */
const collectPseudoTraversalNodes = (
  path: PseudoTraversalPath
): PseudoTraversalNode[] =>
  path.flatMap((triple, index) => {
    const [from, to] = orderByWalkDirection(triple);
    const reachedNode = {
      name: to,
      reachedBy: {
        referenceType: triple.referenceType,
        direction: triple.direction,
      },
    };
    if (index === 0) return [reachedNode];
    const [, previousTo] = orderByWalkDirection(path[index - 1]);
    return normalizeName(from) === normalizeName(previousTo)
      ? [reachedNode]
      : [{ name: from, reachedBy: null }, reachedNode];
  });

const collectComponentTypeNames = ({ componentTypes }: MetaModel): string[] =>
  uniq(componentTypes.map(({ name }) => name));

/** Types whose names differ only in casing are distinct; the same casing wins. */
const findExactComponentTypeNames = (
  name: string,
  componentTypeNames: string[]
): string[] => {
  const sameCaseTypeNames = componentTypeNames.filter(
    typeName => typeName.trim() === name.trim()
  );
  return sameCaseTypeNames.length
    ? sameCaseTypeNames
    : componentTypeNames.filter(
        typeName => normalizeName(typeName) === normalizeName(name)
      );
};

/** Matches by inclusion either way, e.g. "applications" matches "Application". */
const findIncludingComponentTypeNames = (
  name: string,
  componentTypeNames: string[]
): string[] =>
  componentTypeNames.filter(typeName => {
    const typeNameKey = normalizeName(typeName);
    const nameKey = normalizeName(name);
    return typeNameKey.includes(nameKey) || nameKey.includes(typeNameKey);
  });

const findExactNameHits = (
  name: string,
  hits: ComponentHit[]
): ComponentHit[] =>
  hits.filter(hit => normalizeName(hit.name) === normalizeName(name));

/** A contains search also returns longer names; exact names win when there are any. */
const preferExactNameHits = (
  name: string,
  hits: ComponentHit[]
): ComponentHit[] => {
  const exactHits = findExactNameHits(name, hits);
  return exactHits.length ? exactHits : hits;
};

/** When the start set names its component type, only hits of that type start the traversal. */
const filterStartHitsByComponentType = (
  hits: ComponentHit[],
  componentType: string | undefined,
  componentTypeNames: string[]
): ComponentHit[] => {
  if (!componentType) return hits;
  const exactTypeNames = findExactComponentTypeNames(
    componentType,
    componentTypeNames
  );
  const typeNames = new Set(
    exactTypeNames.length
      ? exactTypeNames
      : findIncludingComponentTypeNames(componentType, componentTypeNames)
  );
  return hits.filter(({ typeName }) => typeNames.has(typeName));
};

/**
 * The candidate type names of a pseudo-traversal node, in order of precedence:
 * an exact component type name, the types of components with exactly that
 * name, component type names by inclusion, the types of any component hit.
 * Traversals are built from type names, not type ids, so several types sharing
 * a name are one candidate.
 */
const resolveCandidateTypeNames = (
  name: string,
  componentTypeNames: string[],
  hits: ComponentHit[]
): string[] => {
  const exactTypeNames = findExactComponentTypeNames(name, componentTypeNames);
  if (exactTypeNames.length) return exactTypeNames;
  const exactHits = findExactNameHits(name, hits);
  if (exactHits.length) return uniq(exactHits.map(({ typeName }) => typeName));
  const includingTypeNames = findIncludingComponentTypeNames(
    name,
    componentTypeNames
  );
  return includingTypeNames.length
    ? includingTypeNames
    : uniq(hits.map(({ typeName }) => typeName));
};

const resolveEndType = (path: Path, startType: string): string =>
  path.length ? getLinkedType(path[path.length - 1]) : startType;

const resolveWalkStartType = ({
  sourceType,
  targetType,
  direction,
}: DirectedTripleWithFilters): string =>
  direction === 'outgoing' ? sourceType : targetType;

const splitIntoDirectedSteps = ({
  sourceComponentType,
  referenceType,
  targetComponentType,
}: MetaModelTriple): DirectedTripleWithFilters[] => {
  const triple = {
    sourceType: sourceComponentType,
    referenceType: toBELabel(referenceType),
    targetType: targetComponentType,
  };
  return [
    { ...triple, direction: 'outgoing' },
    { ...triple, direction: 'incoming' },
  ];
};

/** The parent relation and its BE label between the same types are one step. */
const indexWalkSteps = (triples: MetaModelTriple[]): WalkSteps =>
  new Map(
    Object.entries(
      groupBy(
        uniqBy(triples.filter(isConcreteTriple), triple =>
          toTripleKey({
            ...triple,
            referenceType: toBELabel(triple.referenceType),
          })
        ).flatMap(splitIntoDirectedSteps),
        resolveWalkStartType
      )
    )
  );

type ConnectionSearch = {
  steps: WalkSteps;
  fromType: string;
  toTypes: ReadonlySet<string>;
};

/** A step taken from 'previousType' during the search. */
type SearchStep = {
  previousType: string;
  step: DirectedTripleWithFilters;
};

/** Every shortest walk from 'fromType' that ends with the given step. */
const backtrackWalks = (
  incomingSteps: ReadonlyMap<string, SearchStep[]>,
  fromType: string,
  { previousType, step }: SearchStep
): Path[] =>
  (previousType === fromType
    ? [[]]
    : (incomingSteps.get(previousType) ?? []).flatMap(incomingStep =>
        backtrackWalks(incomingSteps, fromType, incomingStep)
      )
  ).map(walk => [...walk, step]);

const findShortestConnectionsFrom = (
  search: ConnectionSearch,
  frontier: string[],
  incomingSteps: ReadonlyMap<string, SearchStep[]>,
  reachedTypes: ReadonlySet<string>,
  hops: number
): Path[] => {
  if (hops > MAX_HOPS_PER_GAP || !frontier.length) return [];
  const { steps, fromType, toTypes } = search;
  const searchSteps = frontier.flatMap(previousType =>
    (steps.get(previousType) ?? []).map(step => ({ previousType, step }))
  );
  const connectingSteps = searchSteps.filter(({ step }) =>
    toTypes.has(getLinkedType(step))
  );
  if (connectingSteps.length) {
    return connectingSteps.flatMap(connectingStep =>
      backtrackWalks(incomingSteps, fromType, connectingStep)
    );
  }
  const newStepsByReachedType = groupBy(
    searchSteps.filter(({ step }) => !reachedTypes.has(getLinkedType(step))),
    ({ step }) => getLinkedType(step)
  );
  const nextFrontier = Object.keys(newStepsByReachedType);
  return findShortestConnectionsFrom(
    search,
    nextFrontier,
    new Map([...incomingSteps, ...Object.entries(newStepsByReachedType)]),
    new Set([...reachedTypes, ...nextFrontier]),
    hops + 1
  );
};

/**
 * All shortest walks of at least one step from 'fromType' to any of 'toTypes'.
 * At least one step, so a type can connect to itself through a self-reference.
 */
const findShortestConnections = (search: ConnectionSearch): Path[] =>
  findShortestConnectionsFrom(
    search,
    [search.fromType],
    new Map(),
    new Set([search.fromType]),
    1
  );

/**
 * Single-step connections with the reference type and direction of the step
 * that reaches the block win over the other connections.
 */
const preferConnectionsMatchingStep = (
  connections: Path[],
  reachedBy: PseudoTraversalStep | null
): Path[] => {
  if (!reachedBy) return connections;
  const matchingConnections = connections.filter(
    ([step, ...furtherSteps]) =>
      !furtherSteps.length &&
      normalizeName(step.referenceType) ===
        normalizeName(reachedBy.referenceType) &&
      step.direction === reachedBy.direction
  );
  return matchingConnections.length ? matchingConnections : connections;
};

/**
 * Root-to-leaf metamodel paths from 'startType' through the blocks in order.
 * A block no path reaches is skipped, and the paths continue from where they were.
 */
const findPathsThroughBlocks = (
  steps: WalkSteps,
  startType: string,
  blocks: ResolvedBlock[]
): Path[] =>
  blocks
    .reduce<Path[]>(
      (paths, { candidateTypeNames, reachedBy }) => {
        const toTypes = new Set(candidateTypeNames);
        const connectionsByEndType = new Map(
          uniq(paths.map(path => resolveEndType(path, startType))).map(
            endType =>
              [
                endType,
                preferConnectionsMatchingStep(
                  findShortestConnections({
                    steps,
                    fromType: endType,
                    toTypes,
                  }),
                  reachedBy
                ),
              ] as const
          )
        );
        const extendedPaths = paths.flatMap(path =>
          (connectionsByEndType.get(resolveEndType(path, startType)) ?? []).map(
            connection => [...path, ...connection]
          )
        );
        return extendedPaths.length ? extendedPaths : paths;
      },
      [[]]
    )
    .filter(path => path.length > 0);

const matchTraversal = ({
  steps,
  startType,
  startHits,
  pathBlocks,
}: TraversalMatching): TraversalMatches => ({
  startType,
  startSet: uniq(
    startHits
      .filter(({ typeName }) => typeName === startType)
      .map(({ id }) => id)
  ),
  pathMatches: pathBlocks.map(({ pseudoTraversalPath, blocks }) => ({
    pseudoTraversalPath,
    metamodelTraversalPaths: findPathsThroughBlocks(steps, startType, blocks),
  })),
});

/** The start names, and the path names that are no exact component type name. */
const collectSearchedNames = (
  { startSet, paths }: PseudoTraversal,
  componentTypeNames: string[]
): string[] =>
  uniqBy(
    [
      ...startSet.startSetInstanceNames,
      ...paths
        .flatMap(collectPseudoTraversalNodes)
        .map(({ name }) => name)
        .filter(
          name => !findExactComponentTypeNames(name, componentTypeNames).length
        ),
    ],
    normalizeName
  );

/**
 * Matches each pseudo-traversal path to metamodel traversal paths, per start
 * type of the start components.
 */
const constructTraversal = ({
  pseudoTraversal: { startSet, paths },
  componentTypeNames,
  steps,
  hitsByNameKey,
}: TraversalConstruction): TraversalMatches[] => {
  const lookUpHits = (name: string): ComponentHit[] =>
    hitsByNameKey.get(normalizeName(name)) ?? [];
  const startHits = filterStartHitsByComponentType(
    uniqBy(startSet.startSetInstanceNames, normalizeName).flatMap(name =>
      preferExactNameHits(name, lookUpHits(name))
    ),
    startSet.componentType,
    componentTypeNames
  );
  const pathBlocks = paths.map(pseudoTraversalPath => ({
    pseudoTraversalPath,
    blocks: collectPseudoTraversalNodes(pseudoTraversalPath)
      .map(({ name, reachedBy }) => ({
        candidateTypeNames: resolveCandidateTypeNames(
          name,
          componentTypeNames,
          lookUpHits(name)
        ),
        reachedBy,
      }))
      .filter(({ candidateTypeNames }) => candidateTypeNames.length > 0),
  }));
  return uniq(startHits.map(({ typeName }) => typeName)).map(startType =>
    matchTraversal({ steps, startType, startHits, pathBlocks })
  );
};

/**
 * The metamodel traversal to display: only one start type has matches, and
 * each of its pseudo-traversal paths has exactly one.
 */
const pickSingleMatchMetamodelTraversal = (
  traversalMatches: TraversalMatches[]
): MetamodelTraversal | null => {
  const matchedTraversals = traversalMatches.filter(hasMatches);
  if (matchedTraversals.length !== 1) return null;
  const [{ startType, startSet, pathMatches }] = matchedTraversals;
  return pathMatches.every(
    ({ metamodelTraversalPaths }) => metamodelTraversalPaths.length === 1
  )
    ? {
        startType,
        startSet,
        paths: pathMatches.map(
          ({ metamodelTraversalPaths }) => metamodelTraversalPaths[0]
        ),
      }
    : null;
};

const collectStartComponents = ({
  data: { componentSelection },
  componentIdsAndReferences: { startSetResult },
  scopeData,
}: TraversalLoadedState) => {
  const startIds = new Set(
    getContextComponentIds(componentSelection, startSetResult)
  );
  return scopeData.components.filter(({ _id }) => startIds.has(_id));
};

/** Each path up to its first wildcard step. */
const collectConcretePaths = (paths: Path[]): Path[] =>
  paths
    .map(path => takeWhile(path, isConcreteStep))
    .filter(path => path.length > 0);

const resolveLoadedTraversalStartType = (
  loadedTraversal: TraversalLoadedState
): string | undefined =>
  pathOperations.getStartTypeFromPaths(
    collectConcretePaths(loadedTraversal.data.paths)
  ) ?? collectStartComponents(loadedTraversal)[0]?.type;

/** A traversal without paths or start components has no start type and is left out. */
const describeLoadedTraversal = (
  loadedTraversal: TraversalLoadedState
): PseudoTraversal[] => {
  const startComponents = collectStartComponents(loadedTraversal);
  const paths = collectConcretePaths(loadedTraversal.data.paths);
  const startType = resolveLoadedTraversalStartType(loadedTraversal);
  if (!startType) return [];
  return [
    {
      loadedTraversalId: loadedTraversal.uuid,
      startSet: {
        componentType: startType,
        startSetInstanceNames: startComponents.map(({ name }) => name),
      },
      paths: paths.map(path =>
        path.map(({ sourceType, referenceType, targetType, direction }) => ({
          sourceTypeOrInstanceName: sourceType,
          referenceType,
          targetTypeOrInstanceName: targetType,
          direction,
        }))
      ),
    },
  ];
};

const describeLoadedTraversals = (
  loadedStates: LoadedState[]
): PseudoTraversal[] =>
  loadedStates.filter(isLoadedTraversal).flatMap(describeLoadedTraversal);

/** Start components of the loaded traversals with this start type, keyed by normalized name. */
const indexLoadedStartComponentsByName = (
  loadedTraversals: TraversalLoadedState[],
  startType: string | undefined
): ReadonlyMap<string, ComponentHit[]> =>
  new Map(
    Object.entries(
      groupBy(
        uniqBy(
          loadedTraversals
            .filter(
              loadedTraversal =>
                resolveLoadedTraversalStartType(loadedTraversal) === startType
            )
            .flatMap(collectStartComponents),
          '_id'
        ).map(({ _id, name, type }) => ({ id: _id, name, typeName: type })),
        ({ name }) => normalizeName(name)
      )
    )
  );

/**
 * The loaded traversal a metamodel traversal with this start type replaces:
 * the one named by 'loadedTraversalId' when it has the start type, otherwise
 * the first with it. None means the metamodel traversal is a new dataset.
 */
const pickEditedLoadedTraversal = (
  loadedTraversals: TraversalLoadedState[],
  startType: string,
  loadedTraversalId: string | undefined
): TraversalLoadedState | null => {
  const sameStartTypeTraversals = loadedTraversals.filter(
    loadedTraversal =>
      resolveLoadedTraversalStartType(loadedTraversal) === startType
  );
  return (
    sameStartTypeTraversals.find(({ uuid }) => uuid === loadedTraversalId) ??
    sameStartTypeTraversals[0] ??
    null
  );
};

/** One key per step: the path up to and including that step, without qualifier and filters. */
const collectPrefixKeys = (path: Path): string[] =>
  path.map((_, index) => pathToKey(path.slice(0, index + 1)));

/**
 * Merges modified paths into the original traversal's: a step whose path up
 * to it is unchanged is the original step, qualifier and filters included.
 * The AI never sees an original path's wildcard steps, so the original path
 * stays as long as the path up to its first wildcard step does.
 */
const mergePaths = (paths: Path[], originalPaths: Path[]): Path[] => {
  const originalStepsByPrefixKey = new Map(
    originalPaths.flatMap(path =>
      collectPrefixKeys(path).map((key, index) => [key, path[index]] as const)
    )
  );
  const mergedPaths = paths.map(path =>
    collectPrefixKeys(path).map(
      (key, index) => originalStepsByPrefixKey.get(key) ?? path[index]
    )
  );
  const mergedPrefixKeys = new Set(mergedPaths.flatMap(collectPrefixKeys));
  const keptWildcardPaths = originalPaths.filter(path => {
    const concretePrefix = takeWhile(path, isConcreteStep);
    return (
      concretePrefix.length < path.length &&
      (concretePrefix.length === 0 ||
        mergedPrefixKeys.has(pathToKey(concretePrefix)))
    );
  });
  const wildcardPrefixKeys = new Set(
    keptWildcardPaths.map(path => pathToKey(takeWhile(path, isConcreteStep)))
  );
  return [
    ...mergedPaths.filter(path => !wildcardPrefixKeys.has(pathToKey(path))),
    ...keptWildcardPaths,
  ];
};

export const traversalConstructionOperations = {
  mergePaths,
  describeLoadedTraversals,
  indexLoadedStartComponentsByName,
  pickEditedLoadedTraversal,
  normalizeName,
  orderByWalkDirection,
  collectPseudoTraversalNodes,
  collectComponentTypeNames,
  preferExactNameHits,
  filterStartHitsByComponentType,
  resolveCandidateTypeNames,
  indexWalkSteps,
  findShortestConnections,
  findPathsThroughBlocks,
  collectSearchedNames,
  constructTraversal,
  pickSingleMatchMetamodelTraversal,
};
